'use client';

import { AnimatePresence, motion } from 'framer-motion';
import type { ComponentProps} from 'react';
import { useState } from 'react';

import { cn, Image } from '@zoomit/primitives';

type Props = { disableSkeleton?: boolean } & ComponentProps<typeof MotionImage>;

const AnimatedImage = (props: Props) => {
  const { blurDataURL, placeholder, objectFit, width, height, disableSkeleton = false, ...rest } = props;
  const [loaded, setLoaded] = useState(false);

  const hasBlurPlaceholder = placeholder === 'blur' && blurDataURL && !loaded;
  const [showSkeleton, setShowSkeleton] = useState(!hasBlurPlaceholder && !disableSkeleton);

  const onImageLoad = () => {
    setLoaded(true);
    showSkeleton && setShowSkeleton(false);
  };

  return (
    <>
      {showSkeleton && (
        <div className="bg-grey6 absolute top-0 left-0 h-full w-full animate-pulse rounded-sm" />
      )}

      <AnimatePresence>
        {hasBlurPlaceholder && (
          <MotionImage
            {...rest}
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            width={width}
            height={height}
            style={width && height ? { aspectRatio: +width / +height } : {}}
            className={cn(
              'absolute !top-1/2 !right-[unset] !left-1/2 !-translate-1/2 !bg-none',
              props.className,
            )}
            placeholder={placeholder}
            src={blurDataURL}
            blurDataURL={blurDataURL}
          />
        )}
      </AnimatePresence>

      <MotionImage
        {...rest}
        width={width}
        height={height}
        onLoad={onImageLoad}
        initial={{ opacity: 0 }}
        whileInView={{ opacity: loaded ? 1 : 0 }}
        transition={{ duration: 0.3 }}
        viewport={{ once: true }}
      />
    </>
  );
};

export default AnimatedImage;

const MotionImage = motion.create(Image);
